import InventoryDisplay from "./content/inventory/InventoryDisplay";
import useToastInventory from "./content/inventory/useToastInventory";
import usePrepPlan from "./content/inventory/usePrepPlan";
import { toast } from "react-toastify";
import { onboardingCompanyStoreId } from "./content/onboarding/handleOnboardingAction";
import React, { useState, useEffect, useCallback, useRef } from "react";
import SettingsDisplay from "./content/settings/SettingsDisplay";
import { SETTINGS_TABS } from "./content/settings/settingsRegistry";
import ContentDisplay2 from "./content/ContentDisplay2";
import NavigationSideBar from "./navbar/NavigationSideBar";
import DailyPlanDisplay from "./content/plan/DailyPlanDisplay";
import RequestsDisplay from "./content/requests/screens/RequestsDisplay";
import RequestsDisplayEmployee from "./content/requests/screens/RequestsDisplayEmployee";
import DashboardDisplay from "./content/reporting/DashboardDisplay";
import DepartmentRoleStationSettingsDisplay from "./content/department_role_station_settings/DepartmentRoleStationSettingsDisplay";
import LocationsIntegrationsHoursSettingsDisplay from "./content/locations_integrations_hours_settings/LocationsIntegrationsHoursSettingsDisplay";
import {
  shouldRefreshMemberStores,
  shouldUndockOther,
} from "./content/operations_assistant/refreshCategories";
import LaborModelOuterDisplay from "./content/labor_model/LaborModelOuterDisplay";
import TimeClockDisplay from "./content/time_clock/TimeClockDisplay";
import TimeEntriesDisplay from "./content/time_clock/TimeEntriesDisplay";
import TipsDisplay from "./content/time_clock/TipsDisplay";
import TipsDisplayEmployee from "./content/time_clock/TipsDisplayEmployee";
import TimeEntriesDisplayEmployee from "./content/time_clock/TimeEntriesDisplayEmployee";
import EmployeesInformationDisplay from "./content/employees_information/EmployeesInformationDisplay";
import PublishedScheduleDisplay from "./content/published_schedule/PublishedScheduleDisplay";
import UserProfileEmployeeDisplay from "./content/employees_information/UserProfileEmployeeDisplay";
import CreateTrainingContentDisplay from "./content/create_training_content/CreateTrainingContentDisplay";
import EngagementDisplay from "./content/engagement/EngagementDisplay";
import TakeTrainingEmployeeDisplay from "./content/take_training_employee/TakeTrainingEmployeeDisplay";
import TrainingOverviewDisplay from "./content/training_overview/TrainingOverviewDisplay";
import TrainingProficiencyDisplay from "./content/training_overview/TrainingProficiencyDisplay";
import ChatDisplay from "./content/chat/ChatDisplay";
import LogbookHubDisplay from "./content/logbook/LogbookHubDisplay";
import MaintenanceDisplay from "./content/maintenance/MaintenanceDisplay";
import BoardsDisplay from "./content/boards/BoardsDisplay";
import OverviewDisplay from "./content/overview/OverviewDisplay";
import KnowledgeHubDisplay from "./content/KnowledgeHubDisplay";
import MobileScreenWarning from "../helpers/MobileScreenWarning";
import NoEnabledFeatures from "../helpers/NoEnabledFeatures";
import AnalyticsHubDisplay from "./content/reporting/AnalyticsHubDisplay";
import PrebuiltReportsDisplay from "./content/reporting/prebuilt/PrebuiltReportsDisplay";
import ContestsDisplay from "./content/contests/ContestsDisplay";
import DeploymentDisplay2 from "./content/deployment/DeploymentDisplay2";
import DeploymentDisplay from "./content/deployment/DeploymentDisplay";
import HeadcountPlanningDisplay from "./content/headcount_planning/HeadcountPlanningDisplay";
import GuestReviewsDisplay2 from "./content/guest_reviews/GuestReviewsDisplay2";
import LiveDayLaborDisplay from "./content/live_day_labor/LiveDayLaborDisplay";
import { API_get_badge_counts, API_create_chat_channel } from "../constants";
import { getRightworkAPI, postRightworkAPI } from "./content/requests/utilities/requests";
import { purgePersistedAuth } from "../utilities/requests";
import useCreateCompanyStore from "../stores/createCompanyStore";
import WelcomeModal from './WelcomeModal';
import ManualSalesReminder from "./ManualSalesReminder";
import LoadSpinner from "../utilities/LoadSpinner";
import SupportFeedbackModal from "./content/home/top_bar_components/SupportFeedbackModal";
import OnboardingCenterDisplay from "./content/onboarding/OnboardingCenterDisplay";
import OnboardingNextStepCard from "./content/onboarding/OnboardingNextStepCard";
import BillingSettingsDisplayConfigurations from "./content/configurations/BillingSettingsDisplayConfigurations";
import CheckInTemplateSettingsDisplayConfigurations from "./content/configurations/CheckInTemplateSettingsDisplayConfigurations";
import InternalBillingOverviewDisplay from "./content/configurations/InternalBillingOverviewDisplay";
import CompanyTeamDisplayConfigurations from "./content/configurations/CompanyTeamDisplayConfigurations";
import OperationsAssistantPanel from "./content/operations_assistant/OperationsAssistantPanel";
import OperationsAssistantFAB from "./content/operations_assistant/OperationsAssistantFAB";

// Badge-count keys from API_get_badge_counts that reflect scheduling/approvals
// workflows (time-off, availability, shift trade/drop, open shifts). These are
// zeroed when scheduling is disabled so no scheduling work surfaces in the nav
// badge, the Operations Assistant FAB, or the panel's suggestion chips.
const SCHEDULING_BADGE_KEYS = [
  "time_off_requests_pending_count",
  "availability_requests_pending_count",
  "shift_trade_requests_pending_count",
  "shift_drop_requests_pending_count",
  "open_shifts_pending_count",
];

const createEmptyOverviewCacheState = () => ({
  companyId: null,
  weekCache: {},
  weekOffset: -1,
  hasCompletedInitialLoad: false,
});

function MainDisplay(props) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [scheduleRenderDate, setScheduleRenderDate] = useState();
  const [overviewCacheState, setOverviewCacheState] = useState(
    createEmptyOverviewCacheState
  );
  const [badgeCounts, setBadgeCounts] = useState();
  const [opsPanelState, setOpsPanelState] = useState("closed");
  const [rovisDockWidth, setRovisDockWidth] = useState(0);
  const [fabPosition, setFabPosition] = useState({ bottom: 24, right: 24 });
  // Lumi opens on demand from the onboarding header.
  const [setupPanelState, setSetupPanelState] = useState("closed");
  const [setupCompanyId, setSetupCompanyId] = useState(null);
  const [setupDockWidth, setSetupDockWidth] = useState(0);
  const setupFabPosition = { bottom: 24, right: 24 };
  const [setupSummaryVersion, setSetupSummaryVersion] = useState(0);
  const bumpSetupSummaryVersion = useCallback(() => {
    setSetupSummaryVersion((n) => n + 1);
  }, []);
  // The checklist derives applicability from the LIVE module flags, so a
  // flag change with the same locations (Training turned on in
  // Configurations) must also make Lumi re-read the summary and come back.
  const storeIdsKey = [
    Array.isArray(props.storeNames)
      ? props.storeNames.map((store) => store.id).sort().join(",")
      : "",
    `sched:${props.schedulingEnabled ? 1 : 0}`,
    `train:${props.trainingEnabled ? 1 : 0}`,
    `docs:${props.documentsEnabled ? 1 : 0}`,
  ].join("|");
  const previousStoreIdsKeyRef = useRef(storeIdsKey);
  useEffect(() => {
    if (previousStoreIdsKeyRef.current === storeIdsKey) return;
    previousStoreIdsKeyRef.current = storeIdsKey;
    bumpSetupSummaryVersion();
  }, [storeIdsKey, bumpSetupSummaryVersion]);
  // Content is pushed right by whichever panel is docked (the wider one if both).
  const opsDockWidth = Math.max(rovisDockWidth, setupDockWidth);
  const [opsRefreshSignal, setOpsRefreshSignal] = useState(null);
  const [scheduleViewingWeek, setScheduleViewingWeek] = useState(null);
  const [
    overviewScheduleNavigationTarget,
    setOverviewScheduleNavigationTarget,
  ] = useState(null);
  const { streamChatUnreadCount = 0, isChatInitializing } = props;
  const { formData, setFormData } = useCreateCompanyStore();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const invalidateOverviewCache = useCallback(() => {
    setOverviewCacheState(createEmptyOverviewCacheState());
  }, []);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Check for initialTab query parameter (e.g., from mobile app redirect)
  // When scheduling is off, the Schedule/Home surfaces are hidden, so we land
  // users on an enabled non-scheduling tab instead. Ordered by priority:
  // training first (preserving the training-first default), then the
  // commonly-on modules, ending on a safe tab that always renders and is NOT
  // gated by the redirect effect below (so a fully-stripped store can't bounce
  // forever). Every conditional branch returns a tab whose flag is on, so the
  // redirect effect never re-blocks the result. These tabs render fine at
  // narrow widths, so the mobileScreenWarning guard is intentionally bypassed.
  const resolveSchedulingOffTab = (role) => {
    if (role === "company_employee") {
      if (props.trainingEnabled) return "takeTrainingEmployee";
      if (props.chatEnabled) return "chat";
      if (props.contestsEnabled) return "contests";
      if (props.documentsEnabled || props.assistantEnabled) return "knowledgeHub";
      return "userProfileEmployee"; // safe empty state, always available
    }
    // Managers / admins
    if (props.trainingEnabled) return "trainingOverview";
    // Dashboard / Overview / Store Analytics are hidden from the sidebar when
    // scheduling is off (and blocked in getBlockedTabs), so they're not valid
    // landing tabs here either — fall through to a still-visible surface.
    if (props.employeesEnabled) return "employeesInformation";
    if (props.chatEnabled) return "chat";
    if (props.logbookEnabled) return "logbook";
    if (props.boardsEnabled) return "boards";
    if (
      props.maintenanceEnabled ||
      props.itTicketsEnabled ||
      props.marketingTicketsEnabled ||
      props.menuTicketsEnabled
    )
      return "maintenance";
    if (props.contestsEnabled) return "contests";
    if (props.documentsEnabled || props.assistantEnabled) return "knowledgeHub";
    // Engagement is admin-only (see NavigationSideBar + getBlockedTabs).
    if (
      props.engagementEnabled &&
      ["rightwork_internal", "company_admin"].includes(role)
    )
      return "engagement";
    // Genuinely safe empty state: a static view that fetches nothing and
    // mounts no feature surface (so it can't expose a disabled module like
    // Knowledge Hub, whose DocumentsDisplay fetches on mount regardless of its
    // flag). Not in the redirect effect's gated list, so it never bounces.
    return "noEnabledFeatures";
  };

  const getDefaultTabForRole = (role) => {
    // Scheduling-off is checked BEFORE the company_timeclock early return:
    // when the master flag hides Time & Payroll, the timeclock kiosk is hidden
    // too, so a kiosk user must land on the safe disabled state rather than the
    // hidden TimeClockDisplay. The kiosk role can't access the manager/employee
    // fallbacks (they're role-gated), so it gets the static empty state
    // directly; other roles resolve to an enabled non-scheduling tab.
    if (props.schedulingEnabled === false) {
      if (role === "company_timeclock") {
        return "noEnabledFeatures";
      }
      return resolveSchedulingOffTab(role);
    }

    if (role === "company_timeclock") {
      return "timeClock";
    }

    if (role === "company_employee") {
      return windowWidth > 600 ? "employeeHome" : "mobileScreenWarning";
    }

    return windowWidth > 600 ? "home" : "mobileScreenWarning";
  };

  // Tabs to reject for the current store based on its feature flags. Shared by
  // getInitialTab (so a ?initialTab= deep link can't mount a hidden surface on
  // the first commit) and the gating effect below (which corrects the tab when
  // a flag flips mid-session). Returns the Set of currently-blocked tab ids.
  // The Tips close is company-wide: reachable when ANY location the manager
  // can see pools tips, not only the home location. Component scope: the
  // sidebar and the tab gate both read it.
  const tipPoolingAnywhere = Boolean(props.tipPoolingEnabled) || (props.storeNames || []).some(
    (store) => Number(store.company) === Number(props.homeCompany) && store.tip_pooling_enabled
  );

  // Read through a ref: getBlockedTabs runs for the initial tab before the
  // prep plan hook below has been called.
  const prepPlanAvailableRef = useRef(false);

  const getBlockedTabs = () => {
    const gatedTabs = [
      { enabled: props.permissionRole === "rightwork_internal" || prepPlanAvailableRef.current, tabs: ["inventory"] },
      {
        enabled: props.permissionOverrides.includes("onboarding_access"),
        tabs: ["onboardingCenter"],
      },
      {
        enabled: props.internalBillingOverviewAccess,
        tabs: ["internalBillingOverview"],
      },
      { enabled: props.overviewEnabled, tabs: ["overview"] },
      { enabled: props.headcountPlanningEnabled, tabs: ["headcountPlanning"] },
      {
        enabled: props.reportingEnabled,
        tabs: ["reporting", "employeeReporting", "prebuiltReports"],
      },
      {
        enabled: ["rightwork_internal", "company_admin"].includes(props.permissionRole),
        tabs: ["laborModel"],
      },
      // Managers get the report emails, which they manage from the Report
      // Library, so they need it too.
      {
        enabled: ["rightwork_internal", "company_admin", "company_manager"].includes(
          props.permissionRole
        ),
        tabs: ["prebuiltReports"],
      },
      { enabled: props.dashboardEnabled, tabs: ["dashboard"] },
      { enabled: props.engagementEnabled, tabs: ["engagement"] },
      {
        enabled: ["rightwork_internal", "company_admin"].includes(props.permissionRole),
        tabs: ["engagement"],
      },
      { enabled: props.approvalsEnabled, tabs: ["actions"] },
      { enabled: props.publishedScheduleEnabled, tabs: ["publishedSchedule"] },
      { enabled: props.employeesEnabled, tabs: ["employeesInformation"] },
      { enabled: props.laborModelEnabled, tabs: ["laborModel"] },
      {
        enabled: props.logbookEnabled,
        tabs: ["logbook", "logbookConfiguration", "logbookReporting"],
      },
      {
        enabled: props.liveLaborEnabled,
        tabs: ["liveDayLabor", "liveDayLaborKiosk"],
      },
      // Training and Company Files can turn off mid-session (starting modules
      // switched from both to scheduling); nobody should stay stranded on
      // a hidden surface with no nav item back.
      {
        enabled: props.trainingEnabled,
        tabs: [
          "trainingOverview",
          "createTrainingContent",
          "takeTrainingEmployee",
          "trainingProficiency",
        ],
      },
      {
        enabled: props.documentsEnabled || props.assistantEnabled,
        tabs: ["knowledgeHub"],
      },
      {
        // Same roles as the sidebar entry: the close endpoints are manager-level.
        enabled: tipPoolingAnywhere && ["rightwork_internal", "company_admin", "company_manager"].includes(props.permissionRole),
        tabs: ["tips"],
      },
      {
        // An employee's own confirmed tips; the endpoint reads only the caller's records.
        enabled: tipPoolingAnywhere && props.permissionRole === "company_employee",
        tabs: ["tipsEmployee"],
      },
      {
        enabled: props.schedulingEnabled,
        tabs: [
          "home",
          "employeeHome",
          "publishedSchedule",
          "actions",
          "liveDayLabor",
          "liveDayLaborKiosk",
          "headcountPlanning",
          "deployment",
          "laborModel",
          "payroll",
          "payrollEmployee",
          "timeClock",
          "plan",
          // Reporting tabs hidden from the sidebar when scheduling is off (see
          // NavigationSideBar's requiresScheduling). Block them here too so a
          // manager can't get stranded on one with no nav item to return to.
          "dashboard",
          "overview",
          "reporting",
          "employeeReporting",
          "guestReviews",
          "prebuiltReports",
        ],
      },
    ];

    const blocked = new Set();
    gatedTabs.forEach(({ enabled, tabs }) => {
      if (enabled === false) tabs.forEach((tab) => blocked.add(tab));
    });
    return blocked;
  };

  // Read mobile-WebView signals from the URL once on mount via useState lazy
  // initializers. We deliberately do NOT call history.replaceState to strip
  // these params: if MainDisplay ever unmounts/remounts (auth state toggle,
  // conditional render flip in App.js), the next mount needs to find the
  // params still in the URL — otherwise `initialTab` falls through to
  // `getDefaultTabForRole`, which returns "mobileScreenWarning" at narrow
  // viewports and bricks the training-tab flow.
  const [bootSignal] = React.useState(() => {
    const params = new URLSearchParams(window.location.search);
    const languageParam = params.get('language');
    if (languageParam) {
      try {
        localStorage.setItem('preferredLanguage', languageParam);
      } catch (_) {}
    }
    return {
      initialTab: params.get('initialTab'),
      trackUuid: params.get('trackUuid') || null,
      embedded: params.get('embedded') === '1',
      companySetupComplete: params.get('companySetup') === 'complete',
    };
  });

  // A manager's ?initialTab=inventory deep link can't be judged until the
  // prep plan probe answers, so hold it instead of treating unknown as
  // blocked. Armed once from the boot URL; the probe effect consumes it.
  const pendingInventoryLinkRef = useRef(
    bootSignal.initialTab === "inventory" && ["company_admin", "company_manager"].includes(props.permissionRole)
  );

  // Deep links to routes removed by consolidation map to their new home so an
  // old ?initialTab= link doesn't land on a tab with no render branch (blank
  // pane). Company Settings was folded into the Locations surface.
  const LEGACY_TAB_REMAP = {
    companySettings: "locationsIntegrationsHoursSettings",
  };

  const getInitialTab = () => {
    // Honor a ?initialTab= deep link only if that tab isn't blocked for this
    // store. Otherwise fall through to the safe default so we never mount a
    // hidden surface (e.g. HomeDisplay2's schedule fetches) on the first commit
    // just to have the gating effect redirect away a tick later.
    const requestedTab =
      LEGACY_TAB_REMAP[bootSignal.initialTab] || bootSignal.initialTab;
    if (requestedTab && !getBlockedTabs().has(requestedTab)) {
      return requestedTab;
    }
    return getDefaultTabForRole(props.permissionRole);
  };

  const [currentTab, setCurrentTabState] = React.useState(getInitialTab());
  const inventory = useToastInventory(props.homeStore, props.permissionRole, currentTab === "inventory");
  const [prepPlanDate, setPrepPlanDate] = React.useState(null);
  const prepPlan = usePrepPlan(props.homeStore, props.permissionRole, currentTab === "inventory", prepPlanDate);
  prepPlanAvailableRef.current = prepPlan.available;
  const settingsDirtyRef = useRef(false);
  const onSettingsDirtyChange = useCallback((value) => { settingsDirtyRef.current = value; }, []);
  const setCurrentTab = useCallback((tab) => {
    if (settingsDirtyRef.current && !window.confirm("Discard unsaved changes?")) return;
    settingsDirtyRef.current = false;
    setCurrentTabState(tab);
  }, []);
  // "Message <name>" from the schedule: the chat tab opens the DM with this
  // user once the Stream client is connected, then clears it.
  const [pendingChatTarget, setPendingChatTarget] = React.useState(null);
  const openDirectMessageWith = React.useCallback(
    (target) => {
      if (!target?.userId) return;
      // Start creating / reusing the 1:1 channel now, so the chat tab only
      // has to watch it once the Stream client is connected.
      const channelIdPromise = postRightworkAPI(API_create_chat_channel, {
        channel_type: "messaging",
        member_ids: [target.userId.toString()],
        store_id: props.homeStore,
      })
        .then((response) => response?.data?.redux?.payload?.channel_id || null)
        .catch(() => null);
      setPendingChatTarget({ ...target, channelIdPromise, storeId: props.homeStore });
      setCurrentTab("chat");
    },
    [props.homeStore]
  );
  // Leaving the Chat tab abandons any pending deep-linked DM, including the
  // case where Stream was still initializing and ChatDisplay never mounted.
  React.useEffect(() => {
    if (currentTab !== "chat") setPendingChatTarget(null);
  }, [currentTab]);
  // "Open employee profile" from the schedule: Team Members opens straight to
  // this store team member, then clears the target.
  const [pendingEmployeeProfileId, setPendingEmployeeProfileId] = React.useState(null);
  const [employeeProfileOrigin, setEmployeeProfileOrigin] = React.useState(null);
  // The schedule view (Week / Day / Month, selected day, month) at the time
  // the profile was opened, restored when the user comes back to it.
  const [scheduleReturnView, setScheduleReturnView] = React.useState(null);
  // True while Live Action was opened from the schedule; gives that tab an
  // X that returns to the schedule. Cleared once the user is anywhere else.
  // The schedule's view snapshot rides along in scheduleReturnView, the same
  // round trip the employee profile uses, so Day / Month and the selected
  // week come back as they were left.
  const [liveActionFromSchedule, setLiveActionFromSchedule] =
    React.useState(false);
  const openLiveActionFromSchedule = React.useCallback((viewState) => {
    setLiveActionFromSchedule(true);
    setScheduleReturnView(viewState || null);
    setCurrentTab("liveDayLabor");
  }, []);
  const closeLiveActionToSchedule = React.useCallback(() => {
    setLiveActionFromSchedule(false);
    setCurrentTab("home");
  }, []);
  React.useEffect(() => {
    if (currentTab !== "liveDayLabor") setLiveActionFromSchedule(false);
  }, [currentTab]);
  const openEmployeeProfile = React.useCallback((storeTeamMemberId, viewState) => {
    if (!storeTeamMemberId) return;
    setPendingEmployeeProfileId(storeTeamMemberId);
    setEmployeeProfileOrigin("schedule");
    setScheduleReturnView(viewState || null);
    setCurrentTab("employeesInformation");
  }, []);
  // True between clicking "Back to schedule" and the schedule mounting with
  // the snapshot; the profile-closed signal must not clear it in that window.
  const returningToScheduleRef = React.useRef(false);
  const consumeScheduleReturnView = React.useCallback(() => {
    returningToScheduleRef.current = false;
    setScheduleReturnView(null);
  }, []);
  React.useEffect(() => {
    // Forget the saved view once the user is anywhere other than the
    // profile (or Live Action opened from the schedule) and the schedule
    // it returns to.
    const holdsReturnView =
      currentTab === "employeesInformation" ||
      currentTab === "home" ||
      (currentTab === "liveDayLabor" && liveActionFromSchedule);
    if (!holdsReturnView) setScheduleReturnView(null);
  }, [currentTab, liveActionFromSchedule]);
  // Back action for a profile opened from the schedule; cleared on use or
  // whenever the user leaves the Team Members tab another way.
  const profileBackToSchedule =
    employeeProfileOrigin === "schedule"
      ? {
          label: "Back to schedule",
          onClick: () => {
            returningToScheduleRef.current = true;
            setEmployeeProfileOrigin(null);
            setCurrentTab("home");
          },
          // "See all employees": stay on Team Members, forget the origin
          onLeave: () => setEmployeeProfileOrigin(null),
        }
      : null;
  React.useEffect(() => {
    if (currentTab !== "employeesInformation") setEmployeeProfileOrigin(null);
  }, [currentTab]);
  const [pendingTrackUuid, setPendingTrackUuid] = React.useState(bootSignal.trackUuid);
  const companySetupHandoffRef = useRef(bootSignal.companySetupComplete);
  const isEmbedded = bootSignal.embedded;
  const consumePendingTrackUuid = useCallback(() => setPendingTrackUuid(null), []);
  const onboardingAccessEnabled = props.permissionOverrides.includes("onboarding_access");

  const fetchBadgeCounts = async () => {
    let api_params = { store_id: props.homeStore };
    let api = API_get_badge_counts;
    try {
      const res = await getRightworkAPI(api, api_params);
      if (res.status === 200) {
        const data = res?.data?.redux?.payload;
        setBadgeCounts(data);
      }
    } catch (error) {
      console.error("Error fetching badge counts:", error);
    }
  };

  // Every mutating assistant tool that changes data a web view renders must
  // map to a refresh category here. Deliberately unmapped (no UI data
  // changes): create_support_request. resend_welcome_message maps to
  // "employees" (it updates invite status on the roster). Setup Assistant
  // tools map to "setup" so
  // the Onboarding Center checklist refreshes; the setup panel refetches its
  // own summary independently.
  const TOOL_NAME_TO_CATEGORY = {
    connect_pos: "pos",
    respond_to_time_off_request: "time_off",
    create_time_off: "time_off",
    cancel_time_off: "time_off",
    update_time_off_type: "time_off",
    create_blackout_dates: "time_off",
    delete_blackout_dates: "time_off",
    approve_shift_pickup: "shift",
    reject_shift_drop: "shift",
    respond_to_availability_request: "availability",
    set_employee_availability: "availability",
    create_shift: "schedule",
    edit_shift: "schedule",
    delete_shift: "schedule",
    copy_week_schedule: "schedule",
    copy_employee_week_schedule: "schedule",
    apply_copilot_recommendations: "schedule",
    publish_schedule: "schedule",
    mark_shift_attendance: "schedule",
    send_channel_message: "messaging",
    send_direct_message: "messaging",
    create_group_chat: "messaging",
    create_logbook_entry: "logbook",
    add_day_note: "events",
    complete_training_block: "training",
    submit_manager_training_input: "training",
    grade_form: "training",
    invite_employee: "employees",
    resend_welcome_message: "employees",
    mark_setup_step: "setup",
    set_business_type: "setup",
    // Applies a feature-flag preset: the member store list is refetched so
    // the nav follows (refreshCategories.MEMBER_STORE_CATEGORIES).
    set_starting_modules: "features",
    send_toast_credential_link: "setup",
    refresh_pos_locations: "pos",
    import_pos_location: "locations",
    create_location: "locations",
    create_role: "roles",
    create_roles: "roles",
    set_departments: "roles",
    retire_sample_location: "locations",
    map_pos_job: "roles",
    import_pos_employees: "employees",
    import_roster: "employees",
    update_employee_info: "employees",
    update_employee_roles: "employees",
    update_employee_permissions: "employees",
    archive_employee: "employees",
    grant_store_access: "employees",
    revoke_store_access: "employees",
    link_pos_employee: "employees",
    update_company_user_profile: "employees",
    save_report_to_dashboard: "saved_reports",
    update_store_settings: "locations",
    set_location_address: "locations",
    set_labor_law_monitoring: "labor_laws",
    set_store_hours: "hours",
    set_store_jurisdiction: "labor_laws",
    set_billing_contact: "billing",
    request_go_live: "billing",
    run_setup_automation: "setup",
    start_recommended_setup: "setup_run",
    confirm_setup_locations: "setup_run",
    retry_recommended_setup: "setup_run",
    create_ticket: "tickets",
    edit_ticket: "tickets",
    comment_on_ticket: "tickets",
    decide_ticket_approval: "tickets",
    auto_assign_daily_tasks: "tasks",
    assign_plan_task: "tasks",
  };

  // Docking is exclusive: when one panel docks, the other (if docked) drops
  // back to its floating default so two panels never share the dock.
  // Each panel owns its dock flag, so the parent compares the widths they
  // report and bumps an undock signal the other panel acts on.
  const [opsUndockSignal, setOpsUndockSignal] = useState(0);
  const [setupUndockSignal, setSetupUndockSignal] = useState(0);
  const dockWidthsRef = useRef({ rovis: 0, setup: 0 });

  const handleOpsDockChange = useCallback((width) => {
    dockWidthsRef.current.rovis = width;
    setRovisDockWidth(width);
    if (shouldUndockOther(width, dockWidthsRef.current.setup)) {
      setSetupUndockSignal((n) => n + 1);
    }
  }, []);

  const handleSetupDockChange = useCallback((width) => {
    dockWidthsRef.current.setup = width;
    setSetupDockWidth(width);
    if (shouldUndockOther(width, dockWidthsRef.current.rovis)) {
      setOpsUndockSignal((n) => n + 1);
    }
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--ops-dock-width", `${opsDockWidth}px`);
  }, [opsDockWidth]);

  // Company onboarding access is company-wide (it is what the setup endpoints
  // authorize), so the current store's role does not gate Lumi.
  const showSetupAssistant =
    Boolean(props.setupAssistantEnabled) &&
    onboardingAccessEnabled &&
    currentTab !== "timeClock" &&
    currentTab !== "liveDayLaborKiosk" &&
    windowWidth > 640;

  const openSetupAssistant = async (companyId) => {
    const targetCompany = companyId || props.homeCompany;
    const targetStore = onboardingCompanyStoreId(targetCompany, props.storeNames);
    if (!targetStore) {
      toast.error("No accessible location found for this company.");
      return;
    }
    try {
      if (Number(targetStore) !== Number(props.homeStore)) {
        await props.change_home_store(targetStore);
      }
    } catch (_) {
      toast.error("Could not open this company. Please try again.");
      return;
    }
    setSetupCompanyId(targetCompany);
    setOpsPanelState("closed");
    setRovisDockWidth(0);
    bumpSetupSummaryVersion();
    setSetupPanelState("default");
  };

  const handleOpsActionConfirmed = (toolNames) => {
    fetchBadgeCounts();
    const categories = [...new Set(
      toolNames.map((name) => TOOL_NAME_TO_CATEGORY[name]).filter(Boolean)
    )];
    if (categories.length > 0) {
      if (categories.includes("schedule") || categories.includes("shift")) {
        invalidateOverviewCache();
      }
      // A store was created or linked through chat: refresh the member store
      // list so the switcher and setup anchor store pick it up.
      if (shouldRefreshMemberStores(categories)) {
        props.refreshMemberStores?.();
      }
      setOpsRefreshSignal({ types: categories, timestamp: Date.now() });
    }
  };

  useEffect(() => {
    if (props.homeStore) {
      fetchBadgeCounts();
    }
  }, [props.homeStore, currentTab]);

  useEffect(() => {
    if (!pendingInventoryLinkRef.current || !prepPlan.checked) return;
    pendingInventoryLinkRef.current = false;
    if (prepPlan.available) setCurrentTab("inventory");
  }, [prepPlan.checked, prepPlan.available, setCurrentTab]);

  useEffect(() => {
    if (getBlockedTabs().has(currentTab)) {
      setCurrentTab(getDefaultTabForRole(props.permissionRole));
    }
  }, [
    props.overviewEnabled,
    props.headcountPlanningEnabled,
    props.reportingEnabled,
    props.dashboardEnabled,
    props.engagementEnabled,
    props.approvalsEnabled,
    props.publishedScheduleEnabled,
    props.employeesEnabled,
    props.laborModelEnabled,
    props.liveLaborEnabled,
    props.schedulingEnabled,
    props.trainingEnabled,
    props.documentsEnabled,
    props.assistantEnabled,
    onboardingAccessEnabled,
    props.internalBillingOverviewAccess,
    tipPoolingAnywhere,
    prepPlan.available,
    currentTab,
    props.permissionRole,
  ]);

  // Restore the company-creation handoff after the emailed password link signs
  // the new administrator in. Consume the URL signal once so later remounts or
  // refreshes do not reopen the modal.
  useEffect(() => {
    if (!companySetupHandoffRef.current) {
      return;
    }
    companySetupHandoffRef.current = false;
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete("companySetup");
    window.history.replaceState(
      {},
      "",
      `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`
    );
    const setupEmail = localStorage.getItem("email") || props.username;
    if (setupEmail) {
      setFormData("email", setupEmail);
    }
    setFormData("setupCompleted", true);
  }, [props.username, setFormData]);

  // New companies land on the checklist; Lumi is available from its header.
  useEffect(() => {
    if (!formData.setupCompleted) return;
    setFormData("setupCompleted", false);
    if (onboardingAccessEnabled) {
      setCurrentTab("onboardingCenter");
    }
    if (!props.setupAssistantEnabled) {
      setShowWelcomeModal(true);
    }

  }, [formData.setupCompleted, onboardingAccessEnabled, props.setupAssistantEnabled, setFormData]);

  // Refresh member stores when entering the Knowledge Hub to get latest permissions
  useEffect(() => {
    if (currentTab === "knowledgeHub" && props.refreshMemberStores) {
      props.refreshMemberStores();
    }
  }, [currentTab]);

  // Shared-iPad kiosk views (Time Clock, Live Action) take over the full screen
  // so employees can't reach manager controls. Without this, a refresh from
  // those views would restore the manager's session from localStorage and drop
  // them into the full app. Purge the persisted auth on explicit entry; the
  // in-memory token in utilities/requests.js keeps the current session's API
  // calls working, but a refresh now lands on the login screen.
  //
  // The initial-mount guard skips URL-driven landings (?initialTab=timeClock,
  // company_timeclock role default, stale bookmarks) so a deep link doesn't
  // silently destroy the session — only a user-initiated tab transition does.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (currentTab === "timeClock" || currentTab === "liveDayLaborKiosk") {
      purgePersistedAuth();
    }
  }, [currentTab]);


  const handleCloseWelcomeModal = () => {
    setShowWelcomeModal(false);
  };

  // API_get_badge_counts returns scheduling/approvals counts unconditionally.
  // When scheduling is disabled those surfaces are hidden, so build ONE
  // sanitized counts object (scheduling keys zeroed) and feed it to every
  // downstream consumer: the derived badge totals, the Operations Assistant
  // FAB, and the panel — whose getSuggestions() reads these keys to render
  // action chips. Non-scheduling counts (training, forms, tickets) are kept.
  const sanitizedBadgeCounts = React.useMemo(() => {
    if (props.schedulingEnabled !== false || !badgeCounts) {
      return badgeCounts;
    }
    const next = { ...badgeCounts };
    SCHEDULING_BADGE_KEYS.forEach((key) => {
      next[key] = 0;
    });
    return next;
  }, [badgeCounts, props.schedulingEnabled]);

  const approvalBadgeCount =
    (sanitizedBadgeCounts?.shift_drop_requests_pending_count || 0) +
    (sanitizedBadgeCounts?.open_shifts_pending_count || 0) +
    (sanitizedBadgeCounts?.shift_trade_requests_pending_count || 0) +
    (sanitizedBadgeCounts?.availability_requests_pending_count || 0) +
    (sanitizedBadgeCounts?.time_off_requests_pending_count || 0);

  const opsAssistantBadgeCount =
    approvalBadgeCount +
    (sanitizedBadgeCounts?.training_actions_pending_count || 0) +
    (sanitizedBadgeCounts?.forms_needing_grading_count || 0);


  function renderTabContent(currentTab, settingsOptions = {}) {
    // Render-level gate: never mount a blocked tab's component, even for the
    // single commit before the gating effect redirects. Any programmatic
    // navigation (ops-assistant "Show me" deep links, onboarding actions,
    // cross-tab buttons) routes through here, so guarding render — not just
    // each setCurrentTab call site — prevents a hidden surface (e.g.
    // HomeDisplay2) from mounting and firing its data fetches. The effect
    // corrects currentTab on the next commit; a blank frame is acceptable.
    if (getBlockedTabs().has(currentTab)) {
      return null;
    }
    if (currentTab === "home") {
      return (
        <>
          {onboardingAccessEnabled && (
            <OnboardingNextStepCard
              homeStore={props.homeStore}
              homeCompany={props.homeCompany}
              change_home_store={props.change_home_store}
              setCurrentTab={setCurrentTab}
            />
          )}
          <ContentDisplay2
            openLiveAction={openLiveActionFromSchedule}
            currentUserId={props.currentUserId}
            scheduleInitialView={scheduleReturnView}
            onScheduleInitialViewConsumed={consumeScheduleReturnView}
            openEmployeeProfile={
              getBlockedTabs().has("employeesInformation") ? undefined : openEmployeeProfile
            }
            openDirectMessageWith={
              props.chatEnabled &&
              (props.permissionRole !== "company_employee" ||
                props.employeeCreateChatEnabled) &&
              (props.permissionRole !== "company_manager" ||
                props.managerCreateChatEnabled)
                ? openDirectMessageWith
                : undefined
            }
            storeOptions={props.storeOptions}
            homeStore={props.homeStore}
            homeCompany={props.homeCompany}
            storeNames={props.storeNames}
            change_home_store={props.change_home_store}
            breakWaiverEnabled={props.breakWaiverEnabled}
            defaultBreakLengthHours={props.defaultBreakLengthHours}
            tplhEnabled={props.tplhEnabled}
            editShiftsInPastEnabled={props.editShiftsInPastEnabled}
            copilotEnabled={props.copilotEnabled}
            windowWidth={windowWidth - opsDockWidth}
            scheduleRenderDate={scheduleRenderDate}
            setScheduleRenderDate={setScheduleRenderDate}
            overviewScheduleNavigationTarget={overviewScheduleNavigationTarget}
            setOverviewScheduleNavigationTarget={
              setOverviewScheduleNavigationTarget
            }
            breakRequiredWhenShiftLengthExceeds={
              props.breakRequiredWhenShiftLengthExceeds
            }
            secondBreakShiftLengthThreshold={
              props.secondBreakShiftLengthThreshold
            }
            proficiencyStarsCount={props.proficiencyStarsCount}
            proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
            printHoursShownEnabled={props.printHoursShownEnabled}
            laborModelEnabled={props.laborModelEnabled}
            laborLawEnabled={props.laborLawEnabled}
            guestReviewsEnabled={props.guestReviewsEnabled}
            liveLaborEnabled={props.liveLaborEnabled}
            fetchBadgeCounts={fetchBadgeCounts}
            permissionRole={props.permissionRole}
            permissionOverrides={props.permissionOverrides}
            publishScheduleReviewRequiredEnabled={
              props.publishScheduleReviewRequiredEnabled
            }
            blockPublishOnLaborViolationsEnabled={
              props.blockPublishOnLaborViolationsEnabled
            }
            paidBreakInputsEnabled={props.paidBreakInputsEnabled}
            businessDeclineShiftOptionEnabled={
              props.businessDeclineShiftOptionEnabled
            }
            manualSalesInputEnabled={props.manualSalesInputEnabled}
            dailyPlanEnabled={props.dailyPlanEnabled}
            opsRefreshSignal={opsRefreshSignal}
            onScheduleWeekChange={setScheduleViewingWeek}
            invalidateOverviewCache={invalidateOverviewCache}
          />
        </>
      );
    }
    if (currentTab === "onboardingCenter") {
      if (!onboardingAccessEnabled) {
        return null;
      }
      return (
        <OnboardingCenterDisplay
          {...settingsOptions}
          homeCompany={props.homeCompany}
          homeStore={props.homeStore}
          change_home_store={settingsOptions.settingsChangeHomeStore || props.change_home_store}
          storeNames={props.storeNames}
          setCurrentTab={settingsOptions.settingsNavigateTab || setCurrentTab}
          opsRefreshSignal={opsRefreshSignal}
          onOpenSetupAssistant={showSetupAssistant ? openSetupAssistant : undefined}
          onProgressChange={bumpSetupSummaryVersion}
          permissionRole={props.permissionRole}
          onFeaturesChanged={props.refreshMemberStores}
        />
      );
    }
    if (currentTab === "overview") {
      return (
        <OverviewDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          laborPercentEnabled={props.laborPercentEnabled}
          schedulingEnabled={props.schedulingEnabled}
          setCurrentTab={setCurrentTab}
          setScheduleRenderDate={setScheduleRenderDate}
          setOverviewScheduleNavigationTarget={
            setOverviewScheduleNavigationTarget
          }
          canOpenSetup={onboardingAccessEnabled}
          overviewCacheState={overviewCacheState}
          setOverviewCacheState={setOverviewCacheState}
        />
      );
    }
    if (currentTab === "contests") {
      return (
        <ContestsDisplay
          permissionRole={props.permissionRole}
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
        />
      );
    }
    if (currentTab === "plan") {
      return (
        <DailyPlanDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          opsRefreshSignal={opsRefreshSignal}
          permissionRole={props.permissionRole}
        />
      );
    }
    if (currentTab === "actions") {
      return (
        <RequestsDisplay
          homeStore={props.homeStore}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          requestMinimumDaysInAdvance={props.requestMinimumDaysInAdvance}
          fetchBadgeCounts={fetchBadgeCounts}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }
    if (currentTab === "employeeHome") {
      return (
        <RequestsDisplayEmployee
          dailyPlanEnabled={props.dailyPlanEnabled}
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          requestMinimumDaysInAdvance={props.requestMinimumDaysInAdvance}
        />
      );
    }
    if (currentTab === "departmentRoleStationSettings") {
      return (
        <DepartmentRoleStationSettingsDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={settingsOptions.settingsChangeHomeStore || props.change_home_store}
          permissionRole={props.permissionRole}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }
    if (currentTab === "headcountPlanning") {
      if (!props.headcountPlanningEnabled) {
        return null;
      }
      return (
        <HeadcountPlanningDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
        />
      );
    }
    if (currentTab === "locationsIntegrationsHoursSettings") {
      return (
        <LocationsIntegrationsHoursSettingsDisplay
          {...settingsOptions}
          homeStore={props.homeStore}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={settingsOptions.settingsChangeHomeStore || props.change_home_store}
          company={props.company}
          permissionRole={props.permissionRole}
          refreshMemberStores={props.refreshMemberStores}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }
    if (currentTab === "billingSettings") {
      return (
        <BillingSettingsDisplayConfigurations
          {...settingsOptions}
          homeCompany={props.homeCompany}
          storeNames={props.storeNames}
          refreshMemberStores={props.refreshMemberStores}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }
    if (currentTab === "checkInTemplate") {
      return (
        <CheckInTemplateSettingsDisplayConfigurations
          homeCompany={props.homeCompany}
        />
      );
    }
    if (currentTab === "internalBillingOverview") {
      if (!props.internalBillingOverviewAccess) {
        return null;
      }
      return <InternalBillingOverviewDisplay />;
    }
    if (currentTab === "companyTeam") {
      return (
        <CompanyTeamDisplayConfigurations
          homeCompany={props.homeCompany}
          storeNames={props.storeNames}
          storeOptions={props.storeOptions}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }
    if (currentTab === "reporting") {
      return (
        <AnalyticsHubDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          userId={props.username}
          aiReportingEnabled={props.aiReportingEnabled}
        />
      );
    }
    // Legacy tab value — the nav item is gone but anything still pointing
    // here lands on the combined Analytics tab with Employee pre-selected.
    if (currentTab === "employeeReporting") {
      return (
        <AnalyticsHubDisplay
          initialView="employee"
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          userId={props.username}
          aiReportingEnabled={props.aiReportingEnabled}
        />
      );
    }
    if (currentTab === "prebuiltReports") {
      return (
        <PrebuiltReportsDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          userId={props.username}
          schedulingEnabled={props.schedulingEnabled}
          permissionRole={props.permissionRole}
        />
      );
    }
    if (currentTab === "dashboard") {
      return (
        <DashboardDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          userId={props.username}
          setCurrentTab={setCurrentTab}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }
    if (currentTab === "guestReviews") {
      return (
        <GuestReviewsDisplay2
          store_id={props.homeStore}
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          change_home_store={props.change_home_store}
        />
      );
    }
    if (currentTab === "laborModel") {
      return (
        <LaborModelOuterDisplay
          {...settingsOptions}
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
        />
      );
    }
    if (currentTab === "payroll") {
      return (
        <TimeEntriesDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          setCurrentTab={setCurrentTab}
          change_home_store={props.change_home_store}
          payrollEnabled={props.payrollEnabled}
          timeEntriesEnabled={props.timeEntriesEnabled}
          payrollWagesEnabled={props.payrollWagesEnabled}
          payrollEmployeeReviewEnabled={props.payrollEmployeeReviewEnabled}
          timeEntriesHoursRoundedToTenths={props.timeEntriesHoursRoundedToTenths}
          timeEntriesRoleSelectionEnabled={props.timeEntriesRoleSelectionEnabled}
          payrollProvider={props.payrollProvider}
          tipCalculatorEnabled={props.tipCalculatorEnabled}
          tipPoolingEnabled={props.tipPoolingEnabled}
          blockManagerSelfTimecardEditEnabled={props.blockManagerSelfTimecardEditEnabled}
          invalidateOverviewCache={invalidateOverviewCache}
        />
      );
    }
    if (currentTab === "tips") {
      return (
        <TipsDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          setCurrentTab={setCurrentTab}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
        />
      );
    }
    if (currentTab === "payrollEmployee") {
      return (
        <TimeEntriesDisplayEmployee
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          setCurrentTab={setCurrentTab}
          change_home_store={props.change_home_store}
          timeEntriesEnabled={props.timeEntriesEnabled}
          payrollEnabled={props.payrollEnabled}
          payrollWagesEnabled={props.payrollWagesEnabled}
          payrollEmployeeReviewEnabled={props.payrollEmployeeReviewEnabled}
          timeEntriesHoursRoundedToTenths={props.timeEntriesHoursRoundedToTenths}
          timeEntriesRoleSelectionEnabled={props.timeEntriesRoleSelectionEnabled}
        />
      );
    }
    if (currentTab === "tipsEmployee") {
      return (
        <TipsDisplayEmployee
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          change_home_store={props.change_home_store}
        />
      );
    }
    if (currentTab === "timeClock") {
      return (
        <TimeClockDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          change_home_store={props.change_home_store}
          timeEntriesRoleSelectionEnabled={
            props.timeEntriesRoleSelectionEnabled
          }
          invalidateOverviewCache={invalidateOverviewCache}
        />
      );
    }
    if (currentTab === "liveDayLabor" || currentTab === "liveDayLaborKiosk") {
      return (
        <LiveDayLaborDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeNames={props.storeNames}
          dailyPlanEnabled={props.dailyPlanEnabled}
          kiosk={currentTab === "liveDayLaborKiosk"}
          setCurrentTab={setCurrentTab}
          onClose={liveActionFromSchedule ? closeLiveActionToSchedule : null}
        />
      );
    }
    if (currentTab === "employeesInformation") {
      return (
        <EmployeesInformationDisplay
          pendingEmployeeProfileId={pendingEmployeeProfileId}
          clearPendingEmployeeProfile={() => setPendingEmployeeProfileId(null)}
          profileBackOverride={profileBackToSchedule}
          onProfileClosed={() => {
            setEmployeeProfileOrigin(null);
            if (!returningToScheduleRef.current) setScheduleReturnView(null);
          }}
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          schedulingEnabled={props.schedulingEnabled}
          timeEntriesEnabled={props.timeEntriesEnabled}
          proficiencyStarsCount={props.proficiencyStarsCount}
          proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
          logbookEnabled={props.logbookEnabled}
          checkInsEnabled={props.checkInsEnabled}
          maintenanceEnabled={props.maintenanceEnabled}
          itTicketsEnabled={props.itTicketsEnabled}
          marketingTicketsEnabled={props.marketingTicketsEnabled}
          menuTicketsEnabled={props.menuTicketsEnabled}
          boardsEnabled={props.boardsEnabled}
          wagesInRightworkEnabled={props.wagesInRightworkEnabled}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }
    if (currentTab === "userProfileEmployee") {
      return (
        <UserProfileEmployeeDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          timeEntriesEnabled={props.timeEntriesEnabled}
        />
      );
    }
    if (currentTab === "publishedSchedule") {
      return (
        <PublishedScheduleDisplay
          homeStore={props.homeStore}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
        />
      );
    }
    if (currentTab === "createTrainingContent") {
      return (
        <CreateTrainingContentDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
        />
      );
    }

    if (currentTab === "engagement") {
      return (
        <EngagementDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
        />
      );
    }

    if (currentTab === "takeTrainingEmployee") {
      return (
        <TakeTrainingEmployeeDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          embedded={isEmbedded}
          pendingTrackUuid={pendingTrackUuid}
          onConsumePendingTrackUuid={consumePendingTrackUuid}
        />
      );
    }

    if (currentTab === "trainingOverview") {
      return (
        <TrainingOverviewDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          proficiencyStarsCount={props.proficiencyStarsCount}
          proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
        />
      );
    }

    if (currentTab === "trainingProficiency") {
      return (
        <TrainingProficiencyDisplay
          homeStore={props.homeStore}
          storeNames={props.storeNames}
          proficiencyStarsCount={props.proficiencyStarsCount}
        />
      );
    }

    if (currentTab === "chat") {
      if (isChatInitializing) {
        return (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <LoadSpinner />
          </div>
        );
      }
      return (
        <ChatDisplay
          homeStore={props.homeStore}
          homeStoreTimezone={props.homeStoreTimezone}
          storeTimezones={props.storeTimezones}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          managerCreateChatEnabled={props.managerCreateChatEnabled}
          employeeCreateChatEnabled={props.employeeCreateChatEnabled}
          pendingChatTarget={pendingChatTarget}
          clearPendingChatTarget={() => setPendingChatTarget(null)}
        />
      );
    }

    if (currentTab === "inventory" && (props.permissionRole === "rightwork_internal" || prepPlan.available)) {
      return <InventoryDisplay
        inventory={inventory}
        prepPlan={prepPlan}
        onPrepDateChange={setPrepPlanDate}
        onPrepDirtyChange={onSettingsDirtyChange}
        internal={props.permissionRole === "rightwork_internal"}
        homeStore={props.homeStore}
        homeCompany={props.homeCompany}
        storeOptions={props.storeOptions}
        storeNames={props.storeNames}
        change_home_store={props.change_home_store}
      />;
    }

    if (currentTab === "logbook") {
      return (
        <LogbookHubDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          laborPercentEnabled={props.laborPercentEnabled}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }

    // Legacy tab value — the "Logbook Categories" nav item is gone; category
    // and layout configuration live on the Logbook hub's Configure tab now.
    if (currentTab === "logbookConfiguration") {
      return (
        <LogbookHubDisplay
          initialView="configure"
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          laborPercentEnabled={props.laborPercentEnabled}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }

    // Legacy tab value — the nav item is gone but anything still pointing
    // here lands on the Logbook tab with Analytics pre-opened (admins only;
    // other roles get the entries view).
    if (currentTab === "logbookReporting") {
      return (
        <LogbookHubDisplay
          initialView="analytics"
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          laborPercentEnabled={props.laborPercentEnabled}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }

    if (currentTab === "maintenance") {
      return (
        <MaintenanceDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          permissionOverrides={props.permissionOverrides}
          maintenanceEnabled={props.maintenanceEnabled}
          itTicketsEnabled={props.itTicketsEnabled}
          marketingTicketsEnabled={props.marketingTicketsEnabled}
          menuTicketsEnabled={props.menuTicketsEnabled}
          opsRefreshSignal={opsRefreshSignal}
        />
      );
    }

    if (currentTab === "boards") {
      return (
        <BoardsDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeNames={props.storeNames}
          storeOptions={props.storeOptions}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          permissionOverrides={props.permissionOverrides}
          boardsEnabled={props.boardsEnabled}
        />
      );
    }

    if (currentTab === "knowledgeHub") {
      return (
        <KnowledgeHubDisplay
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
          permissionOverrides={props.permissionOverrides}
          company={props.company}
          documentsEnabled={props.documentsEnabled}
          assistantEnabled={props.assistantEnabled}
        />
      );
    }

    if (currentTab === "deployment") {
      return (
        <DeploymentDisplay2
          homeStore={props.homeStore}
          homeCompany={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          username={props.username}
          change_home_store={props.change_home_store}
          permissionRole={props.permissionRole}
        />
      );
    }

    if (currentTab === "mobileScreenWarning") {
      return <MobileScreenWarning />;
    }
    if (currentTab === "noEnabledFeatures") {
      return <NoEnabledFeatures />;
    }
  }

  function renderTabDisplay() {
    if (SETTINGS_TABS.includes(currentTab)) {
      return <SettingsDisplay
        key={currentTab}
        {...props}
        initialTab={currentTab}
        onNavigateTab={setCurrentTab}
        renderLegacy={renderTabContent}
        onSettingsDirtyChange={onSettingsDirtyChange}
      />;
    }
    return renderTabContent(currentTab);
  }

  return (
    <div>
      {currentTab !== "timeClock" &&
      currentTab !== "liveDayLaborKiosk" &&
      props.permissionRole !== "company_timeclock" &&
      windowWidth > 640 ? (
        <div>
          <NavigationSideBar
            inventoryAvailable={inventory.available}
            prepPlanAvailable={prepPlan.available}
            currentTab={currentTab}
            setCurrentTab={setCurrentTab}
            username={props.username}
            permissionRole={props.permissionRole}
            proficiencyFromLMSEnabled={props.proficiencyFromLMSEnabled}
            permissionOverrides={props.permissionOverrides}
            homeStore={props.homeStore}
            timeEntriesEnabled={props.timeEntriesEnabled}
            trainingEnabled={props.trainingEnabled}
            engagementEnabled={props.engagementEnabled}
            chatEnabled={props.chatEnabled}
            logbookEnabled={props.logbookEnabled}
            checkInsEnabled={props.checkInsEnabled}
            dailyPlanEnabled={props.dailyPlanEnabled}
            overviewEnabled={props.overviewEnabled}
            approvalsEnabled={props.approvalsEnabled}
            publishedScheduleEnabled={props.publishedScheduleEnabled}
            payrollEnabled={props.payrollEnabled}
            tipPoolingEnabled={tipPoolingAnywhere}
            documentsEnabled={props.documentsEnabled}
            headcountPlanningEnabled={props.headcountPlanningEnabled}
            laborModelEnabled={props.laborModelEnabled}
            schedulingEnabled={props.schedulingEnabled}
            contestsEnabled={props.contestsEnabled}
            assistantEnabled={props.assistantEnabled}
            deploymentEnabled={props.deploymentEnabled}
            guestReviewsEnabled={props.guestReviewsEnabled}
            maintenanceEnabled={props.maintenanceEnabled}
            itTicketsEnabled={props.itTicketsEnabled}
            marketingTicketsEnabled={props.marketingTicketsEnabled}
            menuTicketsEnabled={props.menuTicketsEnabled}
            boardsEnabled={props.boardsEnabled}
            liveLaborEnabled={props.liveLaborEnabled}
            reportingEnabled={props.reportingEnabled}
            dashboardEnabled={props.dashboardEnabled}
            employeesEnabled={props.employeesEnabled}
            internalBillingOverviewAccess={props.internalBillingOverviewAccess}
            approvalBadgeCount={approvalBadgeCount}
            streamChatUnreadCount={streamChatUnreadCount}
          />
          <div
            style={{
              marginLeft: 60 + opsDockWidth,
              minHeight: "100vh",
              backgroundColor: "#fff",
            }}
          >
            {renderTabDisplay()}
          </div>
        </div>
      ) : (
        <div>{renderTabDisplay()}</div>
      )}

      {!showWelcomeModal && currentTab !== "timeClock" && currentTab !== "liveDayLaborKiosk" && (
        <ManualSalesReminder
          store={props.storeNames?.find((store) => String(store.id) === String(props.homeStore))}
          permissionRole={props.permissionRole}
          userId={props.currentUserId ?? props.username}
          bannerLeft={windowWidth > 640 ? 84 + opsDockWidth : 16}
          onSaved={() => {
            invalidateOverviewCache();
            setOpsRefreshSignal({ types: ["sales"], timestamp: Date.now() });
          }}
        />
      )}
      {/* Welcome Modal for New Users */}
      <WelcomeModal
        open={showWelcomeModal}
        onClose={handleCloseWelcomeModal}
        userEmail={formData.email}
        setCurrentTab={setCurrentTab}
      />
      <SupportFeedbackModal homeStore={props.homeStore} />

      {/* Operations Assistant floating panel */}
      {(!showSetupAssistant || setupPanelState === "closed") && props.operationsAssistantEnabled &&
        props.permissionRole !== "company_employee" &&
        props.permissionRole !== "company_timeclock" &&
        currentTab !== "timeClock" &&
        currentTab !== "liveDayLaborKiosk" &&
        windowWidth > 640 && (
          <>
            <OperationsAssistantPanel
              homeStore={props.homeStore}
              homeCompany={props.homeCompany}
              storeNames={props.storeNames}
              schedulingEnabled={props.schedulingEnabled}
              trainingEnabled={props.trainingEnabled}
              setCurrentTab={setCurrentTab}
              username={props.username}
              permissionRole={props.permissionRole}
              badgeCounts={sanitizedBadgeCounts}
              panelState={opsPanelState}
              setPanelState={setOpsPanelState}
              onActionConfirmed={handleOpsActionConfirmed}
              onDockChange={handleOpsDockChange}
              undockSignal={opsUndockSignal}
              fabPosition={fabPosition}
              scheduleViewingWeek={currentTab === "home" ? scheduleViewingWeek : null}
            />
            {opsPanelState === "closed" && (
              <OperationsAssistantFAB
                badgeCount={opsAssistantBadgeCount}
                onClick={() => setOpsPanelState("default")}
                position={fabPosition}
                onPositionChange={setFabPosition}
              />
            )}
          </>
        )}

      {/* Setup Assistant (Lumi) floating panel, company-scoped */}
      {showSetupAssistant && (
        <>
          <OperationsAssistantPanel
            mode="setup"
            homeStore={props.homeStore}
            homeCompany={setupCompanyId || props.homeCompany}
            change_home_store={props.change_home_store}
            storeNames={props.storeNames}
            setCurrentTab={setCurrentTab}
            username={props.username}
            permissionRole={props.permissionRole}
            panelState={setupPanelState}
            setPanelState={setSetupPanelState}
            onActionConfirmed={handleOpsActionConfirmed}
            onOpenOperationsAssistant={
              props.operationsAssistantEnabled ? () => { setSetupPanelState("closed"); setSetupDockWidth(0); setOpsPanelState("default"); } : null
            }
            setupSummaryVersion={setupSummaryVersion}
            onDockChange={handleSetupDockChange}
            undockSignal={setupUndockSignal}
            fabPosition={setupFabPosition}
          />

        </>
      )}
    </div>
  );
}

export default MainDisplay;
